import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, XCircle, Sparkles, Stars } from 'lucide-react';
import { authApi } from '../services/api';
import { useNamespacedTranslation } from '../i18n/index';
import { MixedText } from '../utils/rtl';
import SpaceBackground from './layout/SpaceBackground';
import { AuthContainer } from './layout/PageContainer';
import { SpaceCard, Card, CardHeader, CardTitle, CardContent, CardFooter } from './ui/Card';
import { Button } from './ui/Button';
import { LoadingSpinner } from './ui/LoadingSpinner';

const EmailVerification: React.FC = () => {
  const { t, i18n } = useNamespacedTranslation('auth');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isRTL = i18n.language === 'he';
  
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'resend'>('loading');
  const [message, setMessage] = useState<string>('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get('token');
    
    if (token) {
      // Verify the email with the token
      verifyEmail(token);
    } else {
      // No token provided, show resend form
      setStatus('resend');
    }
  }, [searchParams]);

  const verifyEmail = async (token: string) => {
    try {
      const response = await authApi.verifyEmail({ token });
      setStatus('success');
      setMessage(response.message || t('verify_email.success.message'));
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      
    } catch (err: any) {
      setStatus('error');
      const errorDetail = err.response?.data?.detail;
      
      if (errorDetail?.includes('כבר מאושרת') || errorDetail?.includes('already verified')) {
        setMessage(t('verify_email.error.already_verified'));
      } else {
        setMessage(errorDetail || t('verify_email.error.invalid_token'));
      }
    }
  };

  const handleResendVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setResendLoading(true);
    setResendError(null);
    setResendSuccess(null);

    // Basic email validation
    if (!resendEmail.trim() || !resendEmail.includes('@')) {
      setResendError(t('login.errors.invalid_credentials'));
      setResendLoading(false);
      return;
    }

    try {
      const response = await authApi.resendVerification({ email: resendEmail.trim() });
      setResendSuccess(response.message || t('verify_email.resend.success.message'));
      setResendEmail(''); // Clear form on success
    } catch (err: any) {
      setResendError(err.response?.data?.detail || t('verify_email.error.network_error'));
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Space Background */}
      <SpaceBackground theme="mixed" intensity="cosmic" />
      
      {/* Floating Elements */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-24 right-16 animate-float">
          <Sparkles className="w-8 h-8 text-muted-purple/40" />
        </div>
        <div className="absolute top-40 left-20 animate-float-delayed">
          <Stars className="w-10 h-10 text-vibrant-orange/30" />
        </div>
        <div className="absolute bottom-32 left-32 animate-pulse">
          <div className="w-4 h-4 bg-vibrant-orange/50 rounded-full"></div>
        </div>
      </div>

      {/* Main Content */}
      <AuthContainer className="relative z-10">
        <div className="max-w-md w-full mx-auto space-y-8">
          {/* Header */}
          <div className="text-center space-y-6">
            {/* Logo with glow effect */}
            <div className="flex justify-center">
              <div className="relative group">
                <div className="absolute inset-0 bg-vibrant-orange/20 rounded-full blur-xl group-hover:blur-2xl transition-all duration-300 animate-pulse"></div>
                <img 
                  src="/study-bel-v2.png" 
                  alt="StudyBel Logo" 
                  className="relative w-20 h-20 object-contain group-hover:scale-110 transition-transform duration-300"
                />
              </div>
            </div>
            
            {/* Title Text */}
            <div className="space-y-3">
              <h1 className="text-4xl md:text-5xl font-bold text-bg-primary text-center">
                {isRTL ? (
                  <MixedText className="text-4xl md:text-5xl font-bold text-bg-primary">
                    {status === 'resend' ? `📧 ${t('verify_email.resend.title')}` : `✉️ ${t('verify_email.title')}`}
                  </MixedText>
                ) : (
                  <>{status === 'resend' ? `📧 ${t('verify_email.resend.title')}` : `✉️ ${t('verify_email.title')}`}</>
                )}
              </h1>
              <p className="text-lg md:text-xl text-bg-secondary text-center leading-relaxed">
                <MixedText className="text-lg md:text-xl text-bg-secondary">
                  {status === 'resend' ? t('verify_email.resend.subtitle') : t('verify_email.subtitle')}
                </MixedText>
              </p>
            </div>
          </div>

          {/* Content based on status */}
          {status === 'loading' && (
            <SpaceCard constellation="orion" className="group">
              <CardContent className="text-center py-8">
                <LoadingSpinner variant="astronaut" size="lg" />
                <p className="mt-4 text-surface-secondary">
                  מאמתים את כתובת האימייל שלכם...
                </p>
              </CardContent>
            </SpaceCard>
          )}

          {status === 'success' && (
            <SpaceCard constellation="orion" className="group">
              <CardContent className="text-center py-8 space-y-4">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto" />
                <h2 className="text-xl font-bold text-green-400">
                  {t('verify_email.success.title')}
                </h2>
                <p className="text-surface-secondary">
                  {message}
                </p>
                <p className="text-sm text-green-300">
                  {t('verify_email.success.redirect_message')}
                </p>
              </CardContent>
            </SpaceCard>
          )}

          {status === 'error' && (
            <SpaceCard constellation="orion" className="group">
              <CardContent className="text-center py-8 space-y-4">
                <XCircle className="w-16 h-16 text-red-400 mx-auto" />
                <h2 className="text-xl font-bold text-red-400">
                  {t('verify_email.error.title')}
                </h2>
                <p className="text-surface-secondary">
                  {message}
                </p>
                <div className="space-y-3 pt-4">
                  <Button
                    variant="cosmic"
                    size="md"
                    className="w-full"
                    onClick={() => setStatus('resend')}
                  >
                    {t('verify_email.links.resend_verification')}
                  </Button>
                  <Button
                    variant="secondary"
                    size="md"
                    className="w-full"
                    onClick={() => navigate('/login')}
                  >
                    <div className={`flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} space-x-2`}>
                      <ArrowLeft className="w-4 h-4" />
                      <span>{t('verify_email.links.back_to_login')}</span>
                    </div>
                  </Button>
                </div>
              </CardContent>
            </SpaceCard>
          )}

          {status === 'resend' && (
            <SpaceCard constellation="cassiopeia" className="group">
              <CardHeader>
                <CardTitle className={`text-center text-xl ${isRTL ? 'text-right' : ''}`}>
                  {t('verify_email.resend.title')}
                </CardTitle>
              </CardHeader>

              <form onSubmit={handleResendVerification}>
                <CardContent className="space-y-6">
                  {/* Success Message */}
                  {resendSuccess && (
                    <Card className="border-green-500/50 bg-green-500/10">
                      <CardContent className="text-green-400 text-center py-4">
                        ✅ {resendSuccess}
                      </CardContent>
                    </Card>
                  )}

                  {/* Error Message */}
                  {resendError && (
                    <Card className="border-red-500/50 bg-red-500/10 animate-shake">
                      <CardContent className="text-red-400 text-center py-3">
                        ⚠️ {resendError}
                      </CardContent>
                    </Card>
                  )}

                  {/* Email Field */}
                  <div className="space-y-2">
                    <label 
                      htmlFor="email" 
                      className={`block text-sm font-medium text-surface-secondary ${isRTL ? 'text-right' : ''}`}
                    >
                      {t('verify_email.resend.form.email')}
                    </label>
                    <div className="relative group">
                      <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none transition-colors duration-200`}>
                        <Mail className="h-5 w-5 text-surface-muted group-focus-within:text-vibrant-orange" />
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        required
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        className={`block w-full ${isRTL ? 'pr-10 pl-3 text-right' : 'pl-10 pr-3'} py-3 border-2 border-surface-muted/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-vibrant-orange/50 focus:border-vibrant-orange transition-all duration-200 bg-surface/80 backdrop-blur-sm text-surface-primary placeholder:text-surface-muted hover:border-surface-muted/50`}
                        placeholder={t('verify_email.resend.form.email_placeholder')}
                        dir={isRTL ? 'rtl' : 'ltr'}
                      />
                    </div>
                  </div>
                </CardContent>

                <CardFooter className="flex flex-col space-y-4">
                  {/* Submit Button */}
                  <Button
                    type="submit"
                    variant="gradient"
                    size="lg"
                    className="w-full"
                    disabled={resendLoading || !!resendSuccess}
                    glow
                  >
                    {resendLoading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <LoadingSpinner variant="simple" size="sm" color="orange" />
                        <span>📧 {t('verify_email.resend.form.sending')}</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center space-x-2">
                        <span>📧 {t('verify_email.resend.form.submit')}</span>
                      </div>
                    )}
                  </Button>

                  {/* Back to Login Link */}
                  <div className="w-full text-center">
                    <Button
                      variant="cosmic"
                      size="md"
                      className="w-full"
                      onClick={() => navigate('/login')}
                    >
                      <div className={`flex items-center justify-center ${isRTL ? 'flex-row-reverse' : ''} space-x-2`}>
                        <ArrowLeft className="w-4 h-4" />
                        <span>{t('verify_email.resend.links.back_to_login')}</span>
                      </div>
                    </Button>
                  </div>
                </CardFooter>
              </form>
            </SpaceCard>
          )}
        </div>
      </AuthContainer>
    </div>
  );
};

export default EmailVerification; 